/** Tracks and updates daily task statistics and health metrics in Firestore. */
import { useEffect, useMemo } from 'react';
import { useAuth } from '@/features/auth/useAuth';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useGCal } from '@/features/planning/context/useGCal';
import {
  calculateUncompletedDuration,
  calculateDailyMetrics,
} from '@/features/tasks/utils/views/taskMetricsUtils';
import { getUserStats, saveUserStats } from '@/services/metricsService';

/**
 * Calculates and stores yesterday's uncompleted task duration.
 * This should be called once per day.
 *
 * @param {string} userId
 * @param {Array} allTasks
 * @param {string} todayStr
 */
const syncYesterdayStats = async (userId, allTasks, todayStr) => {
  try {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    // Check if stats for yesterday already exist
    const stats = await getUserStats(userId, yesterdayStr);

    if (stats) {
      localStorage.setItem('lastStatCheck', todayStr);
      return;
    }

    // Calculate and store stats
    const totalDuration = calculateUncompletedDuration(allTasks, userId);
    await saveUserStats(userId, yesterdayStr, {
      uncompletedDuration: totalDuration,
    });

    localStorage.setItem('lastStatCheck', todayStr);
  } catch (error) {
    console.error('Error calculating daily stats:', error);
  }
};

/**
 * A hook to calculate and store daily statistics for the logged-in user.
 * It calculates the total duration of incomplete tasks from the previous day
 * and stores it in a 'stats' subcollection for the user.
 * This process runs only once per day on the user's first session.
 *
 * Also returns today's task progress and project breakdown for the TodayProgressBar component.
 * @returns {{ completedDuration: number, totalDuration: number, percentage: number, projectBreakdown: Array<{ id: string, name: string, completedDuration: number, totalDuration: number }> }}
 */
export const useDailyStats = () => {
  const { user } = useAuth();
  const { allTasks, projectDisplayMap } = useTasksContext();
  const { isEnabled, getEventsForRange } = useGCal();

  useEffect(() => {
    if (!user) return;

    const todayStr = new Date().toISOString().split('T')[0];
    if (localStorage.getItem('lastStatCheck') === todayStr) return;

    syncYesterdayStats(user.uid, allTasks, todayStr);
  }, [user, allTasks]);

  // Get today's GCal events and calculate their total duration
  const gcalEvents = useMemo(() => {
    if (!isEnabled) return [];
    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date();
    dayEnd.setHours(23, 59, 59, 999);
    return getEventsForRange(dayStart, dayEnd) || [];
  }, [isEnabled, getEventsForRange]);

  const gcalDuration = useMemo(() => {
    return gcalEvents.reduce((total, event) => {
      const startTime = new Date(event.start.dateTime || event.start.date);
      const endTime = new Date(event.end.dateTime || event.end.date);
      const duration = Math.ceil(
        (endTime.getTime() - startTime.getTime()) / (1000 * 60),
      );
      return total + Math.max(duration, 0);
    }, 0);
  }, [gcalEvents]);

  // Derived stats for today's progress bar (time-based)
  return useMemo(
    () => calculateDailyMetrics(allTasks, projectDisplayMap, gcalDuration),
    [allTasks, projectDisplayMap, gcalDuration],
  );
};
